import { useState } from 'react';import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Search, X, Package } from 'lucide-react';import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { Checkbox } from '@/app/components/ui/checkbox';
import { useAssets } from '@/hooks/use-assets';
import type { Kit, AssetStatus } from '@/lib/types';
const kitFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters'),
  description: z.string().optional(),
});

type KitFormData = z.infer<typeof kitFormSchema>;

interface KitFormProps {
  kit?: Kit;
  onSubmit: (data: KitFormData & { assets: number[] }) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const assetStatusColors: Record<AssetStatus, string> = {
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  in_use: 'bg-blue-100 text-blue-800',
  retired: 'bg-gray-100 text-gray-800',
  damaged: 'bg-red-100 text-red-800',
  lost: 'bg-red-100 text-red-800',
  active: 'bg-green-100 text-green-800',
  quarantined: 'bg-red-100 text-red-800',
};

export function KitForm({ kit, onSubmit, onCancel, isSubmitting }: KitFormProps) {
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<number>>(
    new Set(kit?.assets || [])
  );
  const [assetSearch, setAssetSearch] = useState('');
  const [assetPage, setAssetPage] = useState(1);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<KitFormData>({
    resolver: zodResolver(kitFormSchema),
    defaultValues: {
      name: kit?.name || '',
      description: kit?.description || '',
    },
  });

  const { data: assetsData, isLoading: assetsLoading } = useAssets({
    page: assetPage,
    page_size: 10,
    q: assetSearch,
  });

  const assets = assetsData?.results || [];
  const totalAssetPages = Math.ceil((assetsData?.count || 0) / 10);

  const handleFormSubmit = async (data: KitFormData) => {
    await onSubmit({
      ...data,
      assets: Array.from(selectedAssetIds),
    });
  };

  const toggleAsset = (assetId: number) => {
    setSelectedAssetIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const removeAsset = (assetId: number) => {
    setSelectedAssetIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(assetId);
      return newSet;
    });
  };

  const selectedAssets = kit?.assets_detail?.filter((a) => selectedAssetIds.has(a.id)) || [];
  const newlySelectedAssets = assets.filter(
    (a) => selectedAssetIds.has(a.id) && !kit?.assets?.includes(a.id)
  );
  const allSelectedAssets = [...selectedAssets, ...newlySelectedAssets];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Kit Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Kit Name *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Enter kit name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Enter kit description (optional)"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Selected Assets ({selectedAssetIds.size})</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedAssetIds.size === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No assets selected. Add assets from the list below.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allSelectedAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Package className="h-4 w-4 text-gray-400" />
                    <div>
                      <Link
                        to={`/assets/${asset.id}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {asset.serial_number}
                      </Link>
                      <p className="text-sm text-gray-500">{asset.display_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={assetStatusColors[asset.status]}>{asset.status}</Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAsset(asset.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add Assets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search assets by serial number or name..."
              value={assetSearch}
              onChange={(e) => {
                setAssetSearch(e.target.value);
                setAssetPage(1);
              }}
              className="pl-10"
            />
          </div>

          {assetsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading assets...</div>
          ) : assets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No assets found.</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Serial Number</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assets.map((asset) => (
                    <TableRow
                      key={asset.id}
                      className={selectedAssetIds.has(asset.id) ? 'bg-blue-50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedAssetIds.has(asset.id)}
                          onCheckedChange={() => toggleAsset(asset.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/assets/${asset.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {asset.serial_number}
                        </Link>
                      </TableCell>
                      <TableCell>{asset.display_name}</TableCell>
                      <TableCell className="capitalize">{asset.type}</TableCell>
                      <TableCell>
                        <Badge className={assetStatusColors[asset.status]}>{asset.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalAssetPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAssetPage((p) => Math.max(1, p - 1))}
                    disabled={assetPage <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {assetPage} of {totalAssetPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAssetPage((p) => Math.min(totalAssetPages, p + 1))}
                    disabled={assetPage >= totalAssetPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : kit ? 'Update Kit' : 'Create Kit'}
        </Button>
      </div>
    </form>
  );
}
